import { create } from 'zustand'
import { ViewType } from '@/stores/useViewAsToggle/useViewAsToggle'
import {
    GHGEmissionsReportSettings,
    TotalEmissionsReportSettings,
    TotalEmissionsReportType,
} from '@/hooks'
import {
    DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS,
    DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS,
    DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS,
} from '@/utils/constants'

export type SustainabilityStoreState = {
    totalEmissions: {
        hasDefaultSettings: boolean
        reportSettings: TotalEmissionsReportSettings
        setReportSettings: (
            settings: Partial<TotalEmissionsReportSettings>
        ) => void
        resetReportSettings: () => void
        selectedView: ViewType
        setSelectedView: (view: ViewType) => void
        hoveredDisplaySetting: TotalEmissionsReportType | null
        setHoveredDisplaySetting: (
            entity: TotalEmissionsReportType | null
        ) => void
    }
    ghgEmissions: {
        hasDefaultSettings: boolean
        reportSettings: GHGEmissionsReportSettings
        setReportSettings: (
            settings: Partial<GHGEmissionsReportSettings>
        ) => void
        resetReportSettings: () => void
    }
    electricityMix: {
        hasDefaultSettings: boolean
        reportSettings: TotalEmissionsReportSettings
        selectedView: ViewType
        setSelectedView: (view: ViewType) => void
        resetReportSettings: () => void
        setReportSettings: (
            settings: Partial<TotalEmissionsReportSettings>
        ) => void
    }
}

const checkDefaultSettings = (settings: TotalEmissionsReportSettings) =>
    JSON.stringify(settings) ===
    JSON.stringify(DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS)

const useSustainabilityStore = create<SustainabilityStoreState>((set) => ({
    totalEmissions: {
        reportSettings: DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS,
        hasDefaultSettings: true,
        hoveredDisplaySetting: null,
        setReportSettings: (
            partialSettings: Partial<TotalEmissionsReportSettings>
        ) => {
            set((state) => {
                const newSettings: TotalEmissionsReportSettings = {
                    ...state.totalEmissions.reportSettings,
                    ...partialSettings,
                }

                return {
                    totalEmissions: {
                        ...state.totalEmissions,
                        reportSettings: newSettings,
                        hasDefaultSettings: checkDefaultSettings(newSettings),
                    },
                }
            })
        },
        resetReportSettings: () => {
            set((state) => ({
                totalEmissions: {
                    ...state.totalEmissions,
                    reportSettings: DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS,
                    hasDefaultSettings: true,
                },
            }))
        },
        selectedView: 'chart',
        setSelectedView: (view) => {
            set((state) => ({
                totalEmissions: {
                    ...state.totalEmissions,
                    selectedView: view,
                },
            }))
        },
        setHoveredDisplaySetting: (entity) => {
            set((state) => ({
                totalEmissions: {
                    ...state.totalEmissions,
                    hoveredDisplaySetting: entity,
                },
            }))
        },
    },
    ghgEmissions: {
        reportSettings: DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS,
        hasDefaultSettings: true,
        setReportSettings: (
            partialSettings: Partial<GHGEmissionsReportSettings>
        ) => {
            set((state) => {
                const newSettings: GHGEmissionsReportSettings = {
                    ...state.ghgEmissions.reportSettings,
                    ...partialSettings,
                }

                return {
                    ghgEmissions: {
                        ...state.ghgEmissions,
                        reportSettings: newSettings,
                        hasDefaultSettings:
                            JSON.stringify(newSettings) ===
                            JSON.stringify(
                                DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS
                            ),
                    },
                }
            })
        },
        resetReportSettings: () => {
            set((state) => ({
                ghgEmissions: {
                    ...state.ghgEmissions,
                    reportSettings: DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS,
                    hasDefaultSettings: true,
                },
            }))
        },
    },
    electricityMix: {
        reportSettings: DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS,
        hasDefaultSettings: true,
        setReportSettings: (
            settings: Partial<TotalEmissionsReportSettings>
        ) => {
            set((state) => {
                const newSettings: TotalEmissionsReportSettings = {
                    ...state.electricityMix.reportSettings,
                    ...settings,
                }

                return {
                    electricityMix: {
                        ...state.electricityMix,
                        reportSettings: newSettings,
                        hasDefaultSettings:
                            JSON.stringify(newSettings) ===
                            JSON.stringify(
                                DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS
                            ),
                    },
                }
            })
        },
        resetReportSettings: () => {
            set((state) => ({
                electricityMix: {
                    ...state.electricityMix,
                    reportSettings: DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS,
                    hasDefaultSettings: true,
                },
            }))
        },
        selectedView: 'chart',
        setSelectedView: (view) => {
            set((state) => ({
                electricityMix: {
                    ...state.electricityMix,
                    selectedView: view,
                },
            }))
        },
    },
}))

export default useSustainabilityStore
